/**
 * LocalStorage Service
 *
 * Centralized service for managing localStorage operations with:
 * - Namespaced keys (climb_log_ prefix)
 * - Type-safe operations
 * - Expiry support
 * - Error handling with fallbacks
 */

interface StorageItem<T> {
  data: T;
  timestamp: number;
  expiresIn?: number; // milliseconds
}

type TitleMode = 'manual' | 'ai' | null;

class LocalStorageService {
  private static instance: LocalStorageService;
  private readonly PREFIX = 'climb_log_';

  // Title Assistant Keys
  readonly MANUAL_TITLES_KEY = 'title_assistant_manual';
  readonly AI_PROMPT_KEY = 'title_assistant_ai_prompt';
  readonly TITLE_MODE_KEY = 'title_assistant_mode';

  private constructor() {}

  static getInstance(): LocalStorageService {
    if (!LocalStorageService.instance) {
      LocalStorageService.instance = new LocalStorageService();
    }
    return LocalStorageService.instance;
  }

  /**
   * Get the prefixed key for localStorage
   */
  private getPrefixedKey(key: string): string {
    return `${this.PREFIX}${key}`;
  }

  /**
   * Generic set method with optional expiry
   */
  set<T>(key: string, value: T, expiresIn?: number): void {
    try {
      const item: StorageItem<T> = {
        data: value,
        timestamp: Date.now(),
        expiresIn
      };
      localStorage.setItem(this.getPrefixedKey(key), JSON.stringify(item));
    } catch (error) {
      console.error(`Failed to set localStorage key "${key}":`, error);
    }
  }

  /**
   * Generic get method with expiry checking
   */
  get<T>(key: string): T | null {
    try {
      const stored = localStorage.getItem(this.getPrefixedKey(key));
      if (!stored) return null;

      const item = JSON.parse(stored) as StorageItem<T>;

      // Check expiry
      if (item.expiresIn && Date.now() - item.timestamp > item.expiresIn) {
        this.remove(key);
        return null;
      }

      return item.data;
    } catch (error) {
      console.error(`Failed to get localStorage key "${key}":`, error);
      return null;
    }
  }

  /**
   * Remove a key from localStorage
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(this.getPrefixedKey(key));
    } catch (error) {
      console.error(`Failed to remove localStorage key "${key}":`, error);
    }
  }

  /**
   * Check if a key exists in localStorage
   */
  has(key: string): boolean {
    try {
      return localStorage.getItem(this.getPrefixedKey(key)) !== null;
    } catch (error) {
      console.error(`Failed to check localStorage key "${key}":`, error);
      return false;
    }
  }

  /**
   * The keys under a prefix and when each was written, oldest first — for a
   * caller that keeps a few large entries and has to let the rest go.
   */
  entries(prefix: string): Array<{ key: string; timestamp: number }> {
    try {
      const fullPrefix = this.getPrefixedKey(prefix);
      return Object.keys(localStorage)
        .filter(key => key.startsWith(fullPrefix))
        .map(key => {
          try {
            const item = JSON.parse(localStorage.getItem(key) || '') as StorageItem<unknown>;
            return { key: key.slice(this.PREFIX.length), timestamp: item.timestamp ?? 0 };
          } catch {
            return { key: key.slice(this.PREFIX.length), timestamp: 0 };
          }
        })
        .sort((a, b) => a.timestamp - b.timestamp);
    } catch (error) {
      console.error(`Failed to list localStorage keys under "${prefix}":`, error);
      return [];
    }
  }

  /**
   * Clear all keys with the climb_log_ prefix
   */
  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }

  // ==========================================
  // Title Assistant Specific Methods
  // ==========================================

  /**
   * Get manual titles array
   */
  getManualTitles(): string[] {
    const titles = this.get<string[]>(this.MANUAL_TITLES_KEY);
    return titles || [];
  }

  /**
   * Set manual titles array (replaces existing)
   */
  setManualTitles(titles: string[]): void {
    // Filter out empty strings and trim
    const cleanTitles = titles
      .map(t => t.trim())
      .filter(t => t.length > 0);

    this.set(this.MANUAL_TITLES_KEY, cleanTitles);
  }

  /**
   * Add manual titles to existing array (merge and deduplicate)
   */
  addManualTitles(newTitles: string[]): void {
    const existing = this.getManualTitles();
    const cleanNewTitles = newTitles
      .map(t => t.trim())
      .filter(t => t.length > 0);

    // Merge and deduplicate using Set
    const merged = [...new Set([...existing, ...cleanNewTitles])];
    this.set(this.MANUAL_TITLES_KEY, merged);
  }

  /**
   * Remove and return the first manual title
   */
  removeFirstManualTitle(): string | null {
    const titles = this.getManualTitles();
    if (titles.length === 0) return null;

    const [first, ...rest] = titles;
    this.setManualTitles(rest);
    return first;
  }

  /**
   * Get AI prompt
   */
  getAIPrompt(): string {
    return this.get<string>(this.AI_PROMPT_KEY) || '';
  }

  /**
   * Set AI prompt
   */
  setAIPrompt(prompt: string): void {
    this.set(this.AI_PROMPT_KEY, prompt.trim());
  }

  /**
   * Get title mode
   */
  getTitleMode(): TitleMode {
    return this.get<TitleMode>(this.TITLE_MODE_KEY);
  }

  /**
   * Set title mode
   */
  setTitleMode(mode: TitleMode): void {
    if (mode === null) {
      this.remove(this.TITLE_MODE_KEY);
    } else {
      this.set(this.TITLE_MODE_KEY, mode);
    }
  }

  // ==========================================
  // Zone Selection Per Gym
  // ==========================================

  /**
   * Get the last selected zone ID for a given gym slug
   */
  getSelectedZoneId(gymSlug: string): string | null {
    return this.get<string>(`zone_${gymSlug}`);
  }

  /**
   * Save the selected zone ID for a given gym slug
   */
  setSelectedZoneId(gymSlug: string, zoneId: string | null): void {
    if (zoneId === null) {
      this.remove(`zone_${gymSlug}`);
    } else {
      this.set(`zone_${gymSlug}`, zoneId);
    }
  }
}

// Export singleton instance
export const localStorageService = LocalStorageService.getInstance();
export type { TitleMode };
